// @ts-strict-ignore
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth, useFeatureFlags } from '@node-space/hooks'
import { ChipProps } from '@node-space/ui-components'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useBulkCapabiltiesQuery } from 'hooks/queries/Capabilities/useCapabiltiesQuery'
import { BulkCapabilityRequest } from 'types/capabilities/capabilities'
import { getWalletActionCapabilityRequestFilters } from 'utils/capabilities'
import { getFiatWalletActionSupport } from 'utils/wallets/fiat'
import { SourcePayWallet } from '../../Pay/types'
import { PayoutBeneficiaryFilters } from '../constants'

interface UseFiatWalletCapabilitiesProps {
  wallet: SourcePayWallet,
  isContactBankAccountFilters?: boolean
}

interface GetFiltersProps {
  supportsWithdrawals: boolean
  supportsPay: boolean
  isB2cPayoutEnabled: boolean
  isContactBankAccountFilters?: boolean
}

export const useWalletBeneficiaryFilters = ({ wallet, isContactBankAccountFilters }: UseFiatWalletCapabilitiesProps) => {
  const { t } = useTranslation()
  const { enableFiatCurrencyFilter, enableFiatPayinCapabilityV2, enableFiatPayoutCapabilityV2 } =
    useFeatureFlags()
  const { currentAccount, isLoading: isLoadingCurrentAccount } = useAccountsContext()
  const { isAuthed } = useAuth()

  const walletProperties = useMemo(() => {
    return {
      currency: wallet?.currency,
      supportsWithdrawals: wallet?.supportsFirstPartyPayouts,
      supportsThirdParty: wallet?.supportsFirstPartyPayouts,
      supportsDeposits: wallet?.supportsPayins,
      lsid: wallet?.walletId,
      isLegacyWallet: !wallet?.supportedTransferDestinations?.length,
    }
  }, [wallet])

  const walletCapabilitiesRequest: BulkCapabilityRequest = useMemo(() => {
    if (!!wallet?.walletId && !!currentAccount?.reference && isAuthed && !isLoadingCurrentAccount) {
      return getWalletActionCapabilityRequestFilters(
        [wallet?.walletId],
        enableFiatPayinCapabilityV2,
        enableFiatPayoutCapabilityV2
      )
    }
  }, [wallet?.walletId, currentAccount?.reference, isAuthed, isLoadingCurrentAccount])

  const {
    data: bulkCapabilities,
    isFetching: isFetchingWalletCapabilities,
    isError: isFetchingWalletCapabilitiesError,
  } = useBulkCapabiltiesQuery(
    walletCapabilitiesRequest,
    (!!walletCapabilitiesRequest && !!wallet?.walletId) && !isContactBankAccountFilters,
    `${wallet?.walletId}-beneficiary-filters`
  )


  const getFilters = ({ supportsWithdrawals, supportsPay, isB2cPayoutEnabled, isContactBankAccountFilters = false }: GetFiltersProps): ChipProps[] => {
    return [
      ...(supportsPay || isContactBankAccountFilters
        ? [
            {
              label: t('wallets.fiatSend.company'),
              name: PayoutBeneficiaryFilters.BUSINESS,
            },
          ]
        : []),
      ...(isB2cPayoutEnabled || isContactBankAccountFilters
        ? [
            {
              label: t('wallets.fiatSend.individual'),
              name: PayoutBeneficiaryFilters.INDIVIDUAL,
            },
          ]
        : []),
      ...(supportsWithdrawals || isContactBankAccountFilters
        ? [
            {
              label: t('wallets.fiatSend.myCompany'),
              name: PayoutBeneficiaryFilters.MY_COMPANY,
            },
          ]
        : []),
    ] as ChipProps[]
  }

  return useMemo(() => {
    const { supportsPay, supportsWithdrawals } = getFiatWalletActionSupport({
      walletProperties,
      bulkCapabilities,
      enableFiatCurrencyFilter,
    })

    const isB2cPayoutEnabled =
      bulkCapabilities?.FIAT?.PAYOUTS_B2C?.WALLET?.[walletProperties?.lsid]?.value === 'true'

    const filters = getFilters({
      supportsWithdrawals,
      supportsPay,
      isB2cPayoutEnabled,
      isContactBankAccountFilters,
    })

    return {
      isLoading: isFetchingWalletCapabilities,
      isError: isFetchingWalletCapabilitiesError,
      filters,
      capabilities: {
        supportsWithdrawals,
        supportsPay,
        isB2cPayoutEnabled,
      },
    }
  }, [
    walletProperties,
    bulkCapabilities,
    enableFiatCurrencyFilter,
    isFetchingWalletCapabilities,
    isFetchingWalletCapabilitiesError,
  ])
}
